import { Injectable } from "@angular/core";
import { AbstractControl, AsyncValidatorFn, ValidationErrors } from "@angular/forms";
import moment from 'moment-mini';
import { constants } from "../models/fiscal-code.costants";
import { Town } from "../models/data-model";
import { RegistrationApiService } from "./registration-api.service";

@Injectable()
export class FiscalCodeService {

    constructor(
        private readonly registrationApiService: RegistrationApiService
    ) { }

    static retrieveBirthDate(fiscalCode: string): { year: number, month: number, day: number } {
        if (!fiscalCode) {
            return null;
        }

        const str_day = fiscalCode.substring(9, 11);
        const str_month = fiscalCode.substring(8, 9).toUpperCase();
        const month_number = constants.MONTH_CODES.indexOf(str_month);
        let str_year = fiscalCode.substring(6, 8);
        let int_day = parseInt(str_day);
        if (int_day > 40) {
            int_day -= 40;
        }
        const int_year = parseInt(str_year);
        const currentFullYear = (new Date()).getFullYear().toString();
        const last_2_digit_currentFullYear = currentFullYear.substring(2);
        if (int_year <= parseInt(last_2_digit_currentFullYear)) {
            str_year = currentFullYear.substring(0, 2) + str_year;
        } else {
            str_year = String(parseInt(currentFullYear.substring(0, 2)) - 1) + str_year;
        }

        const dob = moment([parseInt(str_year), month_number, int_day]);

        return {
            year: dob.year(),
            month: dob.month(),
            day: dob.date()
        };
    }

    static refetrieveSex(fiscalCode: string): "M" | "F" {
        const str_day = fiscalCode.substring(9, 11);
        const int_day = parseInt(str_day);
        if (int_day > 40) {
            return 'F';
        }
        return 'M';
    }

    static stringReplaceAt(str: string, index: number, character: string): string {
        return [str.substring(0, index), character, str.substring(character.length + index)].join('');
    }

    extractTownCode(fiscalCode: string): string {
        if (!fiscalCode) {
            return null;
        }

        let town_code = fiscalCode.substring(11, 15).toUpperCase();
        for (let i = 1; i <= 3; i++) {
            const c = town_code.toUpperCase().charAt(i);
            if (constants.OMOCODIA_TABLE.indexOf(c) !== -1) {
                town_code = FiscalCodeService.stringReplaceAt(town_code, i, constants.OMOCODIA_TABLE.indexOf(c).toString());
            }
        }
        return town_code;
    }

    retrieveBirthPlace(fiscalCode: string): Promise<Town> {
        if (!fiscalCode) {
            return Promise.resolve(null);
        }

        return this.searchTaxCode(this.extractTownCode(fiscalCode))
            .catch(() => {
                return null;
            });
    }

    validator: AsyncValidatorFn = async (control: AbstractControl): Promise<ValidationErrors | null> => {
        const regex: RegExp = /^[a-zA-Z]{6}[l-vL-V0-9]{2}[a-zA-Z][l-vL-V0-9]{2}[a-zA-Z][l-vL-V0-9]{3}[a-zA-Z]$/;
        const cf = control.value;
        if (cf === null || cf === "") {
            return null;
        }
        return (cf
            && regex.test(cf)
            && this.validateCheckDigit(cf)
            && moment(FiscalCodeService.retrieveBirthDate(cf)).isValid()
            && (cf && (cf.charAt(11).toUpperCase() === "Z" || await this.retrieveBirthPlace(cf)))
        ) ? null : { 'wrongFormat': { value: cf } };
    };

    private searchTaxCode(taxCode: string): Promise<Town> {
        return this.registrationApiService.searchItalianTownByTaxCode(taxCode);
    }

    private calculateCheckDigit(codicefiscale: string): string {
        let val = 0;
        const codicefiscale_to_upper = codicefiscale.toUpperCase();
        for (let i = 0; i < 15; i++) {
            const c = codicefiscale_to_upper.charAt(i);
            if (i % 2) {
                val += constants.EVEN_CHECK_DIGITS[c];
            } else {
                val += constants.ODD_CHECK_DIGITS[c];
            }
        }
        val = val % 26;
        return constants.CHECK_DIGITS_MATRIX.charAt(val);
    }

    private validateCheckDigit(codicefiscale: string): boolean {
        return codicefiscale.substring(15).toUpperCase() == this.calculateCheckDigit(codicefiscale);
    }

    validateAgainstLastName(codicefiscale: string, lastName: string): boolean {
        return codicefiscale.toUpperCase().substring(0, 3) == this.buildLastNamePart(lastName);
    }

    validateAgainstFirstName(codicefiscale: string, firstName: string): boolean {
        return codicefiscale.toUpperCase().substring(3, 6) == this.buildFirstNamePart(firstName);
    }

    private buildFirstNamePart(firstName: string): string {
        const normalizedFirstName = this.normalizeString(firstName);
        const consonants: string[] = [];
        const vowels: string[] = [];
        let namePart = ["X", "X", "X"];
        for (let i = 0; i < normalizedFirstName.length; i++) {
            const char = normalizedFirstName.charAt(i);
            if (constants.VOWELS.indexOf(char) > -1) {
                vowels.push(char);
            } else if (constants.CONSONANTS.indexOf(char) > -1) {
                consonants.push(char);
            }
        }
        if (consonants.length > 3) {
            namePart = [consonants[0], consonants[2], consonants[3]];
        } else {
            namePart[0] = consonants.shift() || vowels.shift() || "X";
            namePart[1] = consonants.shift() || vowels.shift() || "X";
            namePart[2] = consonants.shift() || vowels.shift() || "X";
        }
        return namePart.join('');
    }

    private buildLastNamePart(lastName: string): string {
        const normalizedLastName = this.normalizeString(lastName);
        const consonants = [];
        const vowels = [];
        const namePart = ["X", "X", "X"];
        for (let i = 0; i < normalizedLastName.length; i++) {
            const char = normalizedLastName.charAt(i);
            if (constants.VOWELS.indexOf(char) > -1) {
                vowels.push(char);
            } else if (constants.CONSONANTS.indexOf(char) > -1) {
                consonants.push(char);
            }
        }
        namePart[0] = consonants.shift() ?? vowels.shift() ?? "X";
        namePart[1] = consonants.shift() ?? vowels.shift() ?? "X";
        namePart[2] = consonants.shift() ?? vowels.shift() ?? "X";
        return namePart.join('');
    }

    private normalizeString(original: string): string {
        return original.toUpperCase().replace(/\s/, '').normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }
}
